import { WorkoutActivity } from '../../models/types';
import i18n from '../../i18n/i18n-setup';
import { activityKindOf, startOfDay, sumDistance, sumTime } from '../../components/activities/activityHelpers';

const DAY_MS = 86400000;

export interface WeekDay {
  date: Date;
  distance: number;
  isToday: boolean;
}

export interface WeekSummary {
  days: WeekDay[];
  current: WorkoutActivity[];
  previous: WorkoutActivity[];
  distance: number;
  previousDistance: number;
  time: number;
  // Average run pace (min/km); 0 when there were no runs.
  runPace: number;
  previousRunPace: number;
  longest: WorkoutActivity | null;
}

const avgRunPace = (items: WorkoutActivity[]): number => {
  const runs = items.filter(a => activityKindOf(a.workoutType) === 'Run' && a.distance > 0);
  const dist = sumDistance(runs);
  return dist > 0 ? sumTime(runs) / 60 / dist : 0;
};

// Last 7 calendar days ending today, plus the 7 days before for week-over-week deltas.
export const summarizeWeek = (activities: WorkoutActivity[], now = new Date()): WeekSummary => {
  const today = startOfDay(now).getTime();
  const days: WeekDay[] = [];
  for (let i = 6; i >= 0; i--) {
    days.push({ date: new Date(today - i * DAY_MS), distance: 0, isToday: i === 0 });
  }
  const weekStart = today - 6 * DAY_MS;
  const prevStart = weekStart - 7 * DAY_MS;
  const current: WorkoutActivity[] = [];
  const previous: WorkoutActivity[] = [];

  activities.forEach(a => {
    const t = startOfDay(new Date(a.startDate)).getTime();
    if (t >= weekStart && t <= today) {
      current.push(a);
      const day = days[Math.round((t - weekStart) / DAY_MS)];
      if (day) day.distance += a.distance || 0;
    } else if (t >= prevStart && t < weekStart) {
      previous.push(a);
    }
  });

  const longest = current.reduce<WorkoutActivity | null>((best, a) => (!best || a.distance > best.distance ? a : best), null);
  return {
    days,
    current,
    previous,
    distance: sumDistance(current),
    previousDistance: sumDistance(previous),
    time: sumTime(current),
    runPace: avgRunPace(current),
    previousRunPace: avgRunPace(previous),
    longest,
  };
};

export const greetingFor = (now = new Date()): string => {
  const h = now.getHours();
  if (h < 12) return i18n.t('home:greeting.morning');
  if (h < 18) return i18n.t('home:greeting.afternoon');
  return i18n.t('home:greeting.evening');
};
